/* Pantalla de gestion de usuarios (TASK-006) — solo administradores.

DISENO:
  - Tabla con todos los usuarios: usuario, email, rol, estado, restaurantes,
    ultimo ingreso.
  - Acciones por fila: editar rol, activar/desactivar, asignar restaurantes,
    resetear contrasena, eliminar.
  - La contrasena temporal se muestra UNA vez en un panel aparte, con boton de
    copiar. No se vuelve a poder consultar.

DECISIONES DE UX QUE IMPORTAN:
  - Las acciones que el backend va a rechazar (desactivarse a si mismo, dejar el
    sistema sin admins) se DESHABILITAN en la interfaz, con el motivo en el
    titulo. Es mejor no ofrecer una puerta que va a rebotar.
  - Al desactivar, eliminar o resetear se CONFIRMA antes: son acciones con
    consecuencia para otra persona.
  - Los errores del backend se muestran tal cual, porque ya vienen redactados
    para el usuario final.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { PALETTE } from "../lib/palette";

interface UsuarioAdmin {
  id: string;
  username: string;
  email: string;
  name: string | null;
  role: string;
  is_active: boolean;
  must_change_password: boolean;
  last_login_at: string | null;
  created_at: string | null;
  restaurants: string[];
}

interface RolInfo {
  valor: string;
  etiqueta: string;
  descripcion: string;
  es_admin: boolean;
}

const inputBase =
  "px-3 py-2 rounded-md border text-sm w-full";
const btnBase =
  "px-3 py-1.5 rounded-md text-sm font-medium border transition-opacity disabled:opacity-40 disabled:cursor-not-allowed";

function fecha(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("es-PE", { dateStyle: "short", timeStyle: "short" });
}

export default function AdminUsuarios() {
  const { user: yo } = useAuth();
  const [usuarios, setUsuarios] = useState<UsuarioAdmin[]>([]);
  const [roles, setRoles] = useState<RolInfo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [adminsActivos, setAdminsActivos] = useState(0);

  // Panel de alta
  const [nuevo, setNuevo] = useState({
    username: "", email: "", name: "", role: "viewer", restaurants: [] as string[],
  });
  const [creando, setCreando] = useState(false);

  // Contrasena temporal (se muestra una sola vez)
  const [temporal, setTemporal] = useState<{ usuario: string; password: string } | null>(null);

  const [restaurantes, setRestaurantes] = useState<{ slug: string; name: string }[]>([]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [{ data: u }, { data: r }] = await Promise.all([
        apiClient.get<{ usuarios: UsuarioAdmin[]; admins_activos: number }>("/admin/usuarios"),
        apiClient.get<{ roles: RolInfo[] }>("/admin/usuarios/roles"),
      ]);
      setUsuarios(u.usuarios);
      setAdminsActivos(u.admins_activos);
      setRoles(r.roles);
      const { data: rest } = await apiClient.get<{ restaurants: { slug: string; name: string }[] }>(
        "/restaurants/"
      );
      setRestaurantes(rest.restaurants);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { detail?: string } } };
      setError(ax.response?.data?.detail ?? "No se pudieron cargar los usuarios.");
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  /** Ejecuta una accion y recarga; los errores se muestran tal cual. */
  const accion = useCallback(
    async (fn: () => Promise<void>, mensajeOk?: string) => {
      setError(null);
      setAviso(null);
      try {
        await fn();
        if (mensajeOk) setAviso(mensajeOk);
        await cargar();
      } catch (e: unknown) {
        const ax = e as { response?: { status?: number; data?: { detail?: string } } };
        setError(
          ax.response?.data?.detail ??
            (ax.response?.status === 403
              ? "Tu usuario no tiene permisos de administrador."
              : "La operación no se pudo completar.")
        );
      }
    },
    [cargar]
  );

  /** Motivo por el que NO se puede desactivar/eliminar a este usuario. */
  const bloqueo = useCallback(
    (u: UsuarioAdmin): string | null => {
      if (u.id === yo?.id) return "No podés desactivar ni eliminar tu propio usuario.";
      const esUltimoAdmin =
        u.role === "admin" && u.is_active && adminsActivos <= 1;
      if (esUltimoAdmin) return "Es el último administrador activo: el sistema quedaría sin acceso.";
      return null;
    },
    [yo, adminsActivos]
  );

  const crear = useCallback(async () => {
    setCreando(true);
    setError(null);
    setTemporal(null);
    await accion(async () => {
      const { data } = await apiClient.post<{ usuario: UsuarioAdmin; password_temporal: string }>(
        "/admin/usuarios",
        {
          username: nuevo.username,
          email: nuevo.email,
          name: nuevo.name || null,
          role: nuevo.role,
          restaurants: nuevo.restaurants,
        }
      );
      setTemporal({ usuario: data.usuario.username, password: data.password_temporal });
      setNuevo({ username: "", email: "", name: "", role: "viewer", restaurants: [] });
    });
    setCreando(false);
  }, [nuevo, accion]);

  const cambiarRol = (u: UsuarioAdmin, rol: string) =>
    accion(() => apiClient.patch(`/admin/usuarios/${u.id}`, { role: rol }).then(() => {}),
           `Rol de ${u.username} actualizado a ${rol}.`);

  const alternarActivo = (u: UsuarioAdmin) => {
    const texto = u.is_active
      ? `¿Desactivar a ${u.username}? No podrá ingresar y se cerrarán sus sesiones.`
      : `¿Reactivar a ${u.username}?`;
    if (!window.confirm(texto)) return;
    void accion(
      () => apiClient.patch(`/admin/usuarios/${u.id}`, { is_active: !u.is_active }).then(() => {}),
      `${u.username} ${u.is_active ? "desactivado" : "reactivado"}.`
    );
  };

  const resetear = (u: UsuarioAdmin) => {
    if (!window.confirm(
      `¿Generar una contraseña nueva para ${u.username}? La anterior dejará de servir y se cerrarán sus sesiones.`
    )) return;
    void accion(async () => {
      const { data } = await apiClient.post<{ password_temporal: string }>(
        `/admin/usuarios/${u.id}/reset-password`
      );
      setTemporal({ usuario: u.username, password: data.password_temporal });
    });
  };

  const eliminar = (u: UsuarioAdmin) => {
    if (!window.confirm(
      `¿Eliminar a ${u.username}? Si tiene cargas o cierres asociados se desactivará en lugar de borrarse, para no perder la trazabilidad.`
    )) return;
    void accion(async () => {
      const { data } = await apiClient.delete<{
        eliminado: boolean; desactivado?: boolean; motivo?: string;
      }>(`/admin/usuarios/${u.id}`);
      setAviso(data.eliminado ? `${u.username} eliminado.` : data.motivo ?? "Se desactivó.");
    });
  };

  const asignar = (u: UsuarioAdmin, slugs: string[]) =>
    accion(() => apiClient.post(`/admin/usuarios/${u.id}/restaurantes`, { restaurants: slugs }).then(() => {}),
           `Restaurantes de ${u.username} actualizados.`);

  const totalAdmins = useMemo(
    () => usuarios.filter((u) => u.role === "admin" && u.is_active).length,
    [usuarios]
  );

  return (
    <div className="p-6 flex flex-col gap-6 max-w-full">
      <div>
        <h2 className="text-xl font-semibold">Usuarios</h2>
        <p className="text-sm opacity-70">
          {usuarios.length} usuarios · {totalAdmins} administrador{totalAdmins === 1 ? "" : "es"} activo{totalAdmins === 1 ? "" : "s"}
        </p>
      </div>

      {error && (
        <div role="alert" className="p-3 rounded-md text-sm"
             style={{ background: "#fee2e2", color: "#991b1b" }}>{error}</div>
      )}
      {aviso && (
        <div role="status" className="p-3 rounded-md text-sm"
             style={{ background: "#dcfce7", color: "#166534" }}>{aviso}</div>
      )}

      {/* Contrasena temporal: se muestra una sola vez */}
      {temporal && (
        <div className="p-3 rounded-md flex flex-wrap items-center gap-3 text-sm"
             style={{ background: "#fef3c7", color: "#92400e" }}>
          <span>
            Contraseña temporal de <strong>{temporal.usuario}</strong>:
          </span>
          <code className="px-2 py-1 rounded font-mono" style={{ background: "#fff" }}>
            {temporal.password}
          </code>
          <button
            type="button"
            className={btnBase}
            style={{ borderColor: "#92400e" }}
            onClick={() => { void navigator.clipboard.writeText(temporal.password); }}
          >Copiar</button>
          <span className="opacity-80">
            Se muestra una sola vez y debe cambiarse al ingresar.
          </span>
          <button type="button" className="ml-auto underline" onClick={() => setTemporal(null)}>
            Ocultar
          </button>
        </div>
      )}

      {/* Alta */}
      <fieldset className="p-4 rounded-md border flex flex-col gap-3"
                style={{ borderColor: "var(--border, #e5e7eb)" }}>
        <legend className="px-2 text-sm font-semibold">Crear usuario</legend>
        <div className="grid gap-3 md:grid-cols-4">
          <label className="text-sm">
            <span className="font-medium">Usuario</span>
            <input className={inputBase} value={nuevo.username}
                   onChange={(e) => setNuevo({ ...nuevo, username: e.target.value })} />
          </label>
          <label className="text-sm">
            <span className="font-medium">Email</span>
            <input className={inputBase} type="email" value={nuevo.email}
                   onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })} />
          </label>
          <label className="text-sm">
            <span className="font-medium">Nombre</span>
            <input className={inputBase} value={nuevo.name}
                   onChange={(e) => setNuevo({ ...nuevo, name: e.target.value })} />
          </label>
          <label className="text-sm">
            <span className="font-medium">Rol</span>
            <select className={inputBase} value={nuevo.role}
                    onChange={(e) => setNuevo({ ...nuevo, role: e.target.value })}>
              {roles.map((r) => (
                <option key={r.valor} value={r.valor}>{r.etiqueta}</option>
              ))}
            </select>
          </label>
        </div>

        {nuevo.role !== "admin" && (
          <div className="text-sm">
            <span className="font-medium">Restaurantes</span>
            <p className="opacity-70 text-xs mb-1">
              Un usuario con rol {nuevo.role} necesita al menos uno, o no verá ningún dato.
            </p>
            <div className="flex flex-wrap gap-3">
              {restaurantes.map((r) => (
                <label key={r.slug} className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={nuevo.restaurants.includes(r.slug)}
                    onChange={(e) =>
                      setNuevo({
                        ...nuevo,
                        restaurants: e.target.checked
                          ? [...nuevo.restaurants, r.slug]
                          : nuevo.restaurants.filter((s) => s !== r.slug),
                      })
                    }
                  />
                  {r.name}
                </label>
              ))}
            </div>
          </div>
        )}

        <div>
          <button
            type="button"
            className={btnBase}
            style={{ background: PALETTE.primaryDark, color: "#fff", borderColor: PALETTE.primaryDark }}
            disabled={creando || !nuevo.username || !nuevo.email}
            onClick={() => void crear()}
          >{creando ? "Creando…" : "Crear usuario"}</button>
        </div>
      </fieldset>

      {/* Listado */}
      {cargando ? (
        <p className="text-sm opacity-70">Cargando usuarios…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: "var(--border, #e5e7eb)" }}>
                <th className="py-2 pr-3">Usuario</th>
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Rol</th>
                <th className="py-2 pr-3">Restaurantes</th>
                <th className="py-2 pr-3">Estado</th>
                <th className="py-2 pr-3">Último ingreso</th>
                <th className="py-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((u) => {
                const motivo = bloqueo(u);
                return (
                  <tr key={u.id} className="border-b align-top"
                      style={{ borderColor: "var(--border, #f3f4f6)" }}>
                    <td className="py-2 pr-3 font-medium">
                      {u.username}
                      {u.id === yo?.id && <span className="ml-1 opacity-60">(vos)</span>}
                      {u.name && <div className="opacity-60 font-normal">{u.name}</div>}
                    </td>
                    <td className="py-2 pr-3">{u.email}</td>
                    <td className="py-2 pr-3">
                      <select
                        className="px-2 py-1 rounded border text-sm"
                        value={u.role}
                        disabled={!!motivo && u.role === "admin"}
                        title={motivo ?? undefined}
                        onChange={(e) => void cambiarRol(u, e.target.value)}
                      >
                        {roles.map((r) => (
                          <option key={r.valor} value={r.valor}>{r.etiqueta}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-3">
                      {u.role === "admin" ? (
                        <span className="opacity-70">Todos</span>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {restaurantes.map((r) => (
                            <label key={r.slug} className="flex items-center gap-1">
                              <input
                                type="checkbox"
                                checked={u.restaurants.includes(r.slug)}
                                onChange={(e) => {
                                  const slugs = e.target.checked
                                    ? [...u.restaurants, r.slug]
                                    : u.restaurants.filter((s) => s !== r.slug);
                                  if (slugs.length === 0) {
                                    window.alert(
                                      `${u.username} necesita al menos un restaurante, o no verá ningún dato.`
                                    );
                                    return;
                                  }
                                  void asignar(u, slugs);
                                }}
                              />
                              {r.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <span className="px-2 py-0.5 rounded text-xs"
                            style={u.is_active
                              ? { background: "#dcfce7", color: "#166534" }
                              : { background: "#f3f4f6", color: "#6b7280" }}>
                        {u.is_active ? "Activo" : "Inactivo"}
                      </span>
                      {u.must_change_password && (
                        <div className="text-xs mt-1" style={{ color: "#92400e" }}>
                          debe cambiar contraseña
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3">{fecha(u.last_login_at)}</td>
                    <td className="py-2">
                      <div className="flex flex-wrap gap-1">
                        <button
                          type="button" className={btnBase}
                          disabled={!!motivo}
                          title={motivo ?? undefined}
                          onClick={() => alternarActivo(u)}
                        >{u.is_active ? "Desactivar" : "Activar"}</button>
                        <button
                          type="button" className={btnBase}
                          onClick={() => resetear(u)}
                        >Resetear clave</button>
                        <button
                          type="button" className={btnBase}
                          disabled={!!motivo}
                          title={motivo ?? undefined}
                          style={{ color: "#991b1b", borderColor: "#fecaca" }}
                          onClick={() => eliminar(u)}
                        >Eliminar</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
